import { z } from '../zod.js';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { AddressValidationSchema } from '../shared/addressValidationSchema';
import { PaginatedListSchema } from '../shared/paginatedList';
import { SubmissionStatusValidationEnum } from './enum';
import { CHARGE_ORDER_STATUS, ChargeOrderStatusValidationEnum } from '../charge/enum';

const SubmissionAddressValidationSchema = AddressValidationSchema.meta({
  id: 'SubmissionAddress',
  description: 'Address details captured on the submission.',
});

const SubmissionBillingValidationSchema = z
  .object({
    name: z
      .string()
      .nullish()
      .meta({ description: 'Billing contact name captured on the submission.' }),
    companyName: z
      .string()
      .nullish()
      .meta({ description: 'Billing company name captured on the submission.' }),
    email: z.string().nullish().meta({ description: 'Billing email captured on the submission.' }),
    billingEmail: z
      .string()
      .nullish()
      .meta({ description: 'Alternate billing email captured on the submission.' }),
    phone: z
      .string()
      .nullish()
      .meta({ description: 'Billing phone number captured on the submission.' }),
    address: SubmissionAddressValidationSchema.nullish().meta({
      description: 'Billing address. This is separate from `shipping.address`.',
    }),
  })
  .meta({ id: 'SubmissionBilling', description: 'Billing details captured on the submission.' });

const SubmissionShippingValidationSchema = z
  .object({
    name: z
      .string()
      .nullish()
      .meta({ description: 'Shipping recipient name captured on the submission.' }),
    phone: z
      .string()
      .nullish()
      .meta({ description: 'Shipping phone number captured on the submission.' }),
    address: SubmissionAddressValidationSchema.nullish().meta({
      description: 'Shipping address. This is separate from the top-level billing information.',
    }),
  })
  .meta({ id: 'SubmissionShipping', description: 'Shipping details captured on the submission.' });

const SubmissionFieldValidationSchema = z
  .object({
    fieldId: z.string().nullish().meta({ description: 'Field ID for this submitted value.' }),
    label: z.string().nullish().meta({ description: 'Field label shown to the submitter.' }),
    value: z.string().nullish().meta({
      description: 'Submitted value rendered as a string.',
    }),
    values: z.array(z.string()).nullish().meta({
      description:
        'Chosen option values of a multi-select field (an option without a value is named by its label), in the order the options are defined. `value` holds the same values joined with ", ". The matching labels, as they were when the answer was written, are in `meta.selectedOptionLabels` and joined in `meta.selectedOptionLabel`. Absent on every other field type.',
    }),
    element: z
      .string()
      .nullish()
      .meta({ description: 'Form element type used to collect this value.' }),
    type: z.string().nullish().meta({ description: 'Field data type for this submitted value.' }),
    meta: z.record(z.string(), z.unknown()).nullish().meta({
      description:
        'Additional structured metadata stored with the submitted field value. Shape varies by field type, so inspect defensively. A `select` or `multi-select` answer keeps the chosen option labels in `selectedOptionLabel` (joined with ", " on a multi-select), and a `multi-select` also lists them in `selectedOptionLabels`, index for index with `values`.',
    }),
    reference: z
      .string()
      .nullish()
      .meta({ description: 'Stable field reference used to identify the field across systems.' }),
  })
  .meta({ id: 'SubmissionField', description: 'A single field value captured on the submission.' });

export const SubmissionResponseValidationSchema = z
  .object({
    id: z
      .string()
      .meta({ description: 'Unique submission record ID.', example: '6812fe6e9f39b6760576f01c' }),
    status: SubmissionStatusValidationEnum.meta({ description: 'Submission processing status.' }),
    // Defaulted so the MCP, which deploys on merge, still parses an API that predates the field.
    orderStatus: ChargeOrderStatusValidationEnum.default('active').meta({
      description: `Lifecycle status set by the seller. Valid values: ${CHARGE_ORDER_STATUS.join(', ')}. \`canceled\` means the seller canceled the submission: it no longer counts toward "Prevent duplicate submissions" and its files left the customer portal.`,
    }),
    canceledAt: z.iso.datetime().nullish().meta({
      description: 'ISO 8601 timestamp when the submission was canceled.',
    }),
    canceledReason: z.string().nullish().meta({
      description: 'The reason given when the submission was canceled.',
    }),
    canceledBy: z.string().nullish().meta({
      description:
        'ID of the dashboard user who canceled the submission. Absent when it was canceled through the seller API.',
    }),
    canceledByApiKey: z.string().nullish().meta({
      description:
        'ID of the API key that canceled the submission, when it was canceled through the seller API. The key may have been deleted since; `canceledByApiKeyName` keeps its name.',
    }),
    canceledByApiKeyName: z.string().nullish().meta({
      description:
        'Name of the API key that canceled the submission, as it was at the time. Kept when the key is deleted later.',
    }),
    sellerId: z.string().nullish().meta({ description: 'Seller ID that owns this submission.' }),
    pageId: z
      .string()
      .nullish()
      .meta({ description: 'Page ID for the form that received this submission.' }),
    customerId: z.string().nullish().meta({
      description: 'Associated customer ID, if the submission is linked to a customer record.',
    }),
    customerEmail: z
      .string()
      .nullish()
      .meta({ description: 'Customer email captured on the submission.' }),
    customerName: z.string().nullish().meta({
      description:
        'Customer name captured on the submission. This can differ from `billing.name` or `shipping.name`.',
    }),
    formTitle: z.string().nullish().meta({
      description: 'Public title of the form that received the submission.',
    }),
    billing: SubmissionBillingValidationSchema.nullish().meta({
      description: 'Billing details captured on the submission.',
    }),
    shipping: SubmissionShippingValidationSchema.nullish().meta({
      description: 'Shipping details captured on the submission.',
    }),
    fields: z
      .array(SubmissionFieldValidationSchema)
      .nullish()
      .meta({ description: 'Submitted form field values in display order.' }),
    livemode: z.boolean().nullish().meta({
      description:
        'Whether the submission was created in live mode (`true`) or test mode (`false`).',
    }),
    locale: z
      .string()
      .nullish()
      .meta({ description: 'Locale active when the submission was created.' }),
    sessionId: z
      .string()
      .nullish()
      .meta({ description: 'Checkout/session ID associated with the submission.' }),
    clientIp: z
      .string()
      .nullish()
      .meta({ description: 'Client IP address recorded at submission time.' }),
    userAgent: z
      .string()
      .nullish()
      .meta({ description: 'Client user agent string recorded at submission time.' }),
    screenWidth: z
      .number()
      .nullish()
      .meta({ description: 'Client screen width recorded at submission time.' }),
    visitId: z
      .string()
      .nullish()
      .meta({ description: 'Tracked visit ID associated with the submission.' }),
    queryParameters: z.record(z.string(), z.string()).nullish().meta({
      description:
        'Query parameters captured from the page URL at submission time. Values are stringified in API output.',
    }),
    pageSlug: z
      .string()
      .nullish()
      .meta({ description: 'Page slug for the form that received the submission.' }),
    createdAt: z.iso
      .datetime()
      .meta({ description: 'ISO 8601 timestamp when the submission was created.' }),
    updatedAt: z.iso
      .datetime()
      .meta({ description: 'ISO 8601 timestamp when the submission was last updated.' }),
  })
  .meta({ id: 'Submission', description: 'A single form submission returned by the seller API.' });

export const SubmissionDetailResponseValidationSchema = z
  .object({
    data: SubmissionResponseValidationSchema,
  })
  .meta({ id: 'SubmissionDetailResponse', description: 'Envelope containing one submission.' });

export const GetSubmissionsResponseValidationSchema = PaginatedListSchema(
  SubmissionResponseValidationSchema,
).meta({ id: 'SubmissionsListResponse', description: 'Paginated submission list response.' });

export const SubmissionToolDataValidationSchema = SubmissionResponseValidationSchema.extend({
  dashboardUrl: DashboardUrlField,
});

export const SubmissionDetailToolResponseValidationSchema = z.object({
  data: SubmissionToolDataValidationSchema,
});

export const SubmissionsToolResponseValidationSchema = PaginatedListSchema(
  SubmissionToolDataValidationSchema,
);
